import { buildLadder } from './season'
import type {
  ClubProjectDefinition,
  ClubProjectId,
  ClubProjectMetric,
  ClubProjectState,
  ClubStrategyState,
  GameState,
  ObjectiveStatus,
  SeasonObjective,
  StakeholderGroup,
  StakeholderState,
  StandingPolicies,
} from './types'

export const CLUB_PROJECT_ABANDONMENT_COST = 8

export const CLUB_PROJECT_CATALOGUE: ClubProjectDefinition[] = [
  {
    id: 'build-through-youth',
    title: 'Build Through Youth',
    promise: 'Establish a senior pathway for young players through earned match exposure.',
    tradeOff: 'Short-term continuity may give way to development opportunities.',
    metric: 'young-player-appearances',
    target: 20,
    branches: [
      { id: 'academy-pathway', title: 'Academy Pathway', promise: 'Broaden opportunity across the emerging group.', tradeOff: 'Exposure is spread across more developing players.' },
      { id: 'senior-mentors', title: 'Senior Mentors', promise: 'Pair young players with stable senior units.', tradeOff: 'Fewer young players can be introduced together.' },
    ],
  },
  {
    id: 'restore-football-standards',
    title: 'Restore Football Standards',
    promise: 'Re-establish repeatable winning standards across a full season.',
    tradeOff: 'Selection patience narrows when weekly execution falls short.',
    metric: 'season-wins',
    target: 6,
    branches: [
      { id: 'contest-first', title: 'Contest First', promise: 'Anchor standards in repeatable contest work.', tradeOff: 'Outside development receives less emphasis.' },
      { id: 'selection-accountability', title: 'Selection Accountability', promise: 'Tie opportunity closely to weekly execution.', tradeOff: 'Continuity is less protected.' },
    ],
  },
  {
    id: 'elite-development-system',
    title: 'Elite Development System',
    promise: 'Turn allocated development work into documented player improvement.',
    tradeOff: 'Capacity committed to long-term growth cannot solve every weekly problem.',
    metric: 'ability-upgrades',
    target: 8,
    branches: [
      { id: 'specialist-coaching', title: 'Specialist Coaching', promise: 'Deepen role-specific teaching.', tradeOff: 'Cross-line flexibility develops more slowly.' },
      { id: 'whole-player', title: 'Whole-player Development', promise: 'Build adaptable players across connected skills.', tradeOff: 'Individual upgrades may take longer.' },
    ],
  },
  {
    id: 'establish-tactical-identity',
    title: 'Establish Tactical Identity',
    promise: 'Build a coherent system the playing group can execute under pressure.',
    tradeOff: 'Repeated system work reduces room for opponent-specific reinvention.',
    metric: 'tactical-cohesion',
    target: 75,
    branches: [
      { id: 'system-mastery', title: 'System Mastery', promise: 'Commit to a stable core method.', tradeOff: 'Adaptation is deliberately constrained.' },
      { id: 'adaptive-principles', title: 'Adaptive Principles', promise: 'Preserve principles while widening responses.', tradeOff: 'Execution demands increase.' },
    ],
  },
  {
    id: 'immediate-contention',
    title: 'Immediate Contention',
    promise: 'Convert the current list and resources into premiership-point pressure now.',
    tradeOff: 'Youth exposure and institutional reserves may receive less protection.',
    metric: 'premiership-points',
    target: 28,
    branches: [
      { id: 'home-ground-edge', title: 'Home-ground Edge', promise: 'Prioritise bankable home results.', tradeOff: 'Away experimentation remains limited.' },
      { id: 'finals-proof', title: 'Finals-proof Football', promise: 'Prepare for high-pressure opposition and conditions.', tradeOff: 'Some regular-season matchups receive less tailoring.' },
    ],
  },
]

const DEFAULT_POLICIES: StandingPolicies = {
  selectionPreference: 'merit',
  welfareRisk: 'balanced',
  researchPosture: 'balanced',
  institutionalPosture: 'balanced',
}

const clamp = (value: number) => Math.max(0, Math.min(100, Math.round(value)))
const seasonNumber = (state: Pick<GameState, 'season' | 'recruitment'>) => state.season?.season ?? state.recruitment.season ?? 1
const averageListAge = (state: Pick<GameState, 'club'>) => state.club.players.length
  ? state.club.players.reduce((total, player) => total + player.age, 0) / state.club.players.length
  : 25

const objectiveStatus = (current: number, target: number, metric: SeasonObjective['type'], seasonComplete: boolean): ObjectiveStatus => {
  const achieved = metric === 'ladder-position' ? current > 0 && current <= target : current >= target
  if (achieved) return 'achieved'
  if (seasonComplete) return 'missed'
  return current > 0 ? 'in-progress' : 'not-started'
}

type ObjectiveContext = Pick<GameState, 'club' | 'season'> & Partial<Pick<GameState, 'matchResult' | 'consequenceReport'>>

const objectiveProgress = (state: ObjectiveContext, metric: SeasonObjective['type'], target: number, baseline: number = 0) => {
  const finalRound = state.season?.fixtures.length ? Math.max(...state.season.fixtures.map(fixture => fixture.round)) : 10
  const seasonComplete = Boolean(state.season && state.season.completedThroughRound >= finalRound)
  let current = 0
  if (metric === 'institutional-capital') current = clamp(state.consequenceReport?.resourceLedger.institutional ?? state.club.resources.institutional)
  if (metric === 'young-player-appearances') current = state.club.players
    .filter(player => player.age <= 22)
    .reduce((total, player) => total + player.development.seniorMatches, 0)
  if (metric === 'ladder-position' && state.season && (state.season.completedThroughRound > 0 || state.matchResult)) {
    const throughRound = Math.max(state.season.completedThroughRound, state.matchResult?.round ?? 0)
    current = buildLadder(state.season, throughRound).findIndex(row => row.teamId === state.season!.userTeamId) + 1
  }
  return { current: current - baseline, target, status: objectiveStatus(current - baseline, target, metric, seasonComplete) }
}

type StrategyContext = Pick<GameState, 'club' | 'season' | 'history' | 'recruitment'>

export const deriveSeasonObjective = (state: StrategyContext): SeasonObjective => {
  const season = seasonNumber(state)
  const completedSeasons = state.history.seasons.filter(summary => summary.season < season)
  const prior = [...completedSeasons].sort((a, b) => b.season - a.season)[0]
  const youngList = averageListAge(state) <= 23
  let definition: Omit<SeasonObjective, 'progress' | 'status'>
  let baseline = 0

  if (youngList) {
    baseline = state.club.players
      .filter(player => player.age <= 22)
      .reduce((total, player) => total + player.development.seniorMatches, 0)
    definition = {
      season,
      id: `s${season}-establish-youth`,
      type: 'young-player-appearances',
      title: 'Establish the next senior core',
      rationale: `The list averages ${averageListAge(state).toFixed(1)} years; opportunity must become demonstrated senior exposure.`,
      target: { metric: 'young-player-appearances', value: 30 },
      reward: 'Meeting the target demonstrates a credible pathway while preserving institutional capital.',
      risk: 'Missing the target leaves the list profile untested at senior level.',
    }
  } else if (prior && prior.kingsport.position <= 2) {
    const ladder = state.season ? buildLadder(state.season) : undefined
    baseline = ladder ? ladder.findIndex(row => row.teamId === state.season!.userTeamId) + 1 : 0
    definition = {
      season,
      id: `s${season}-sustain-contention`,
      type: 'ladder-position',
      title: 'Sustain top-two contention',
      rationale: `Kingsport finished ${prior.kingsport.position}${prior.kingsport.position === 1 ? 'st' : 'nd'} last season; the standard is now repeatable contention.`,
      target: { metric: 'ladder-position', value: 2 },
      reward: 'A top-two finish validates the club system and protects institutional capital.',
      risk: 'Falling outside the top two increases scrutiny of whether last season was sustainable.',
    }
  } else if (prior && prior.kingsport.position >= Math.ceil(prior.finalLadder.length * 2 / 3)) {
    const target = Math.max(1, prior.kingsport.position - 2)
    const ladder = state.season ? buildLadder(state.season) : undefined
    baseline = ladder ? ladder.findIndex(row => row.teamId === state.season!.userTeamId) + 1 : 0
    definition = {
      season,
      id: `s${season}-climb-ladder`,
      type: 'ladder-position',
      title: `Climb to ${target}${target === 1 ? 'st' : target === 2 ? 'nd' : target === 3 ? 'rd' : 'th'} or better`,
      rationale: `Last season's ${prior.kingsport.position}th-place finish requires a measurable two-position response.`,
      target: { metric: 'ladder-position', value: target },
      reward: 'The ladder improvement restores confidence without inventing a separate success measure.',
      risk: 'Failure to climb two positions places institutional capital under pressure.',
    }
  } else {
    baseline = clamp(state.club.resources.institutional)
    const target = Math.min(100, Math.max(60, baseline + 6))
    definition = {
      season,
      id: `s${season}-strengthen-institution`,
      type: 'institutional-capital',
      title: `Build institutional capital to ${target}`,
      rationale: `${completedSeasons.length} completed season${completedSeasons.length === 1 ? '' : 's'} and current institutional capital of ${baseline} call for a measurable club-wide foundation.`,
      target: { metric: 'institutional-capital', value: target },
      reward: 'Reaching the target gives the club greater resilience around future football decisions.',
      risk: 'Missing the target leaves less patience for setbacks.',
    }
  }

  const progress = objectiveProgress(state, definition.type, definition.target.value, baseline)
  return { ...definition, progress: { current: progress.current, target: progress.target, baseline }, status: progress.status }
}

const stakeholder = (confidence: number, influence: number, evidence: string): StakeholderState => ({
  confidence: clamp(confidence),
  trend: 'stable',
  influence,
  evidence,
})

const emptyProject = (abandonmentCost = 0): ClubProjectState => ({
  selectedId: null,
  selectedSeason: null,
  status: 'unselected',
  stage: 'foundation',
  progress: { metric: null, current: 0, target: 0 },
  selectionBaseline: null,
  branchReady: false,
  availableBranches: [],
  selectedBranchId: null,
  branchSelectionSeason: null,
  abandonmentCost,
  promise: '',
  tradeOff: '',
})

export const createClubStrategy = (state: StrategyContext): ClubStrategyState => {
  const institutional = clamp(state.club.resources.institutional)
  const season = seasonNumber(state)
  return {
    version: 52,
    stakeholders: {
      board: stakeholder(institutional, 90, 'Board confidence reflects current institutional capital.'),
      supporters: stakeholder(institutional, 75, 'Supporter belief begins from the club’s institutional standing.'),
      seniorPlayers: stakeholder(institutional, 80, 'Senior-player trust begins from the club’s institutional standing.'),
      youngPlayers: stakeholder(institutional, 65, 'Young-player trust begins from the club’s institutional standing.'),
      sponsors: stakeholder(institutional, 60, 'Sponsor confidence begins from the club’s institutional standing.'),
    },
    objective: deriveSeasonObjective(state),
    project: emptyProject(),
    policies: { ...DEFAULT_POLICIES },
    annualPlan: { season, locked: false, confirmed: false },
    processedRounds: [],
  }
}

export const hydrateClubStrategy = (state: StrategyContext, saved?: ClubStrategyState): ClubStrategyState => {
  const season = seasonNumber(state)
  if (!saved || ![51, 52].includes((saved as { version?: number }).version ?? 0)) return createClubStrategy(state)
  const baseline = createClubStrategy(state)
  return {
    ...baseline,
    ...saved,
    version: 52,
    stakeholders: { ...baseline.stakeholders, ...saved.stakeholders },
    policies: { ...baseline.policies, ...saved.policies },
    objective: saved.objective?.season === season ? { ...saved.objective, progress: { ...saved.objective.progress, baseline: saved.objective.progress.baseline ?? 0 } } : deriveSeasonObjective(state),
    project: {
      ...baseline.project,
      ...saved.project,
      selectionBaseline: saved.project.selectionBaseline ?? null,
      branchSelectionSeason: saved.project.branchSelectionSeason ?? null,
    },
    annualPlan: saved.annualPlan?.season === season ? saved.annualPlan : { season, locked: false, confirmed: false },
    processedRounds: Array.isArray(saved.processedRounds) ? [...new Set(saved.processedRounds)] : [],
  }
}

const catalogueEntry = (id: ClubProjectId) => CLUB_PROJECT_CATALOGUE.find(project => project.id === id)

export const selectClubProject = (state: GameState, projectId: ClubProjectId): GameState => {
  if (state.clubStrategy.annualPlan.locked) return state
  const definition = catalogueEntry(projectId)
  if (!definition) return state
  const current = currentProjectMetric(state, definition.metric)
  return {
    ...state,
    clubStrategy: {
      ...state.clubStrategy,
      project: {
        selectedId: projectId,
        selectedSeason: seasonNumber(state),
        status: 'active',
        stage: 'foundation',
        progress: { metric: definition.metric, current: 0, target: definition.target },
        selectionBaseline: current,
        branchReady: false,
        availableBranches: definition.branches.map(branch => ({ ...branch })),
        selectedBranchId: null,
        branchSelectionSeason: null,
        abandonmentCost: 0,
        promise: definition.promise,
        tradeOff: definition.tradeOff,
      },
    },
  }
}

export const abandonClubProject = (state: GameState): GameState => {
  if (state.clubStrategy.annualPlan.locked || !state.clubStrategy.project.selectedId) return state
  return {
    ...state,
    club: {
      ...state.club,
      resources: {
        ...state.club.resources,
        institutional: clamp(state.club.resources.institutional - CLUB_PROJECT_ABANDONMENT_COST),
      },
    },
    clubStrategy: { ...state.clubStrategy, project: emptyProject(CLUB_PROJECT_ABANDONMENT_COST) },
  }
}

export const updateStandingPolicies = (state: GameState, changes: Partial<StandingPolicies>): GameState => {
  if (state.clubStrategy.annualPlan.locked) return state
  return { ...state, clubStrategy: { ...state.clubStrategy, policies: { ...state.clubStrategy.policies, ...changes } } }
}

export const lockAnnualClubPlan = (state: GameState): GameState => {
  const policySelectionsAreValid = ['merit', 'youth-opportunity', 'continuity'].includes(state.clubStrategy.policies.selectionPreference)
    && ['cautious', 'balanced', 'aggressive'].includes(state.clubStrategy.policies.welfareRisk)
    && ['targeted', 'balanced', 'broad'].includes(state.clubStrategy.policies.researchPosture)
    && ['protect', 'balanced', 'spend-to-win'].includes(state.clubStrategy.policies.institutionalPosture)
  if (state.clubStrategy.annualPlan.locked || !state.clubStrategy.project.selectedId || !catalogueEntry(state.clubStrategy.project.selectedId) || !policySelectionsAreValid) return state
  return {
    ...state,
    clubStrategy: {
      ...state.clubStrategy,
      annualPlan: { season: seasonNumber(state), locked: true, confirmed: true },
    },
  }
}

const currentProjectMetric = (state: GameState, metric: ClubProjectMetric) => {
  if (metric === 'young-player-appearances') return state.club.players.filter(player => player.age <= 22).reduce((sum, player) => sum + player.development.seniorMatches, 0)
  if (metric === 'ability-upgrades') return state.club.players.reduce((sum, player) => sum + player.development.history.filter(entry => Boolean(entry.upgrade)).length, 0)
  if (metric === 'tactical-cohesion') return Math.round(state.club.tacticalCohesion)
  const userRow = state.season ? buildLadder(state.season).find(row => row.teamId === state.season!.userTeamId) : undefined
  if (metric === 'season-wins') return userRow?.wins ?? state.club.record.wins
  return userRow?.premiershipPoints ?? 0
}

export const progressClubProject = (state: GameState): GameState => {
  const definition = state.clubStrategy.project.selectedId ? catalogueEntry(state.clubStrategy.project.selectedId) : undefined
  if (!definition || state.clubStrategy.project.status !== 'active') return state
  const current = currentProjectMetric(state, definition.metric)
  const baseline = state.clubStrategy.project.selectionBaseline ?? 0
  const relativeCurrent = current - baseline
  const ratio = definition.target ? relativeCurrent / definition.target : 0
  const complete = relativeCurrent >= definition.target
  return {
    ...state,
    clubStrategy: {
      ...state.clubStrategy,
      project: {
        ...state.clubStrategy.project,
        status: complete ? 'complete' : 'active',
        stage: complete ? 'complete' : ratio >= 0.5 ? 'established' : 'foundation',
        progress: { metric: definition.metric, current: relativeCurrent, target: definition.target },
        branchReady: ratio >= 0.5,
        availableBranches: definition.branches.map(branch => ({ ...branch })),
      },
    },
  }
}

const refreshObjectiveProgress = (state: GameState): SeasonObjective => {
  const objective = state.clubStrategy.objective
  const baseline = objective.progress.baseline ?? 0
  const progress = objectiveProgress(state, objective.type, objective.target.value, baseline)
  return { ...objective, progress: { current: progress.current, target: progress.target, baseline }, status: progress.status }
}

const objectiveMovement = (before: SeasonObjective, after: SeasonObjective) => {
  if (before.status !== 'achieved' && after.status === 'achieved') return 2
  if (before.type === 'ladder-position') {
    if (before.progress.current === 0 || after.progress.current === 0) return 0
    return after.progress.current < before.progress.current ? 1 : after.progress.current > before.progress.current ? -1 : 0
  }
  return after.progress.current > before.progress.current ? 1 : after.progress.current < before.progress.current ? -1 : 0
}

export const updateClubStrategyAfterRound = (state: GameState, completedRound = state.matchResult?.round): GameState => {
  if (completedRound === undefined) return state
  const roundKey = `s${seasonNumber(state)}-r${completedRound}`
  if (state.clubStrategy.processedRounds.includes(roundKey)) return state
  const stakeholders = Object.fromEntries(Object.entries(state.clubStrategy.stakeholders).map(([group, value]) => [group, { ...value }])) as ClubStrategyState['stakeholders']
  const groupsWithDirectEvidence = new Set<StakeholderGroup>()
  for (const change of state.consequenceReport?.institutionalChanges ?? []) {
    if (change.group === 'assistants' || !(change.group in stakeholders)) continue
    const group = change.group as StakeholderGroup
    groupsWithDirectEvidence.add(group)
    stakeholders[group] = {
      ...stakeholders[group],
      confidence: clamp(stakeholders[group].confidence + change.change),
      trend: change.change > 0 ? 'rising' : change.change < 0 ? 'falling' : 'stable',
      evidence: change.reason,
    }
  }
  const objective = refreshObjectiveProgress(state)
  const movement = objectiveMovement(state.clubStrategy.objective, objective)
  if (movement !== 0) {
    for (const group of ['board', 'sponsors'] as const) {
      if (groupsWithDirectEvidence.has(group)) continue
      stakeholders[group] = {
        ...stakeholders[group],
        confidence: clamp(stakeholders[group].confidence + movement),
        trend: movement > 0 ? 'rising' : 'falling',
        evidence: movement > 0
          ? `Season objective progress improved to ${objective.progress.current} against a target of ${objective.progress.target}.`
          : `Season objective progress moved away from the target of ${objective.progress.target}.`,
      }
    }
  }
  const next = {
    ...state,
    clubStrategy: {
      ...state.clubStrategy,
      stakeholders,
      objective,
      processedRounds: [...state.clubStrategy.processedRounds, roundKey],
    },
  }
  return progressClubProject(next)
}

export const selectClubProjectBranch = (state: GameState, branchId: string): GameState => {
  if (state.clubStrategy.annualPlan.locked) return state
  const project = state.clubStrategy.project
  if (!project.selectedId || !project.branchReady) return state
  const definition = catalogueEntry(project.selectedId)
  if (!definition) return state
  const branch = definition.branches.find(b => b.id === branchId)
  if (!branch) return state
  const remainingBranches = definition.branches.filter(b => b.id !== branchId)
  return {
    ...state,
    clubStrategy: {
      ...state.clubStrategy,
      project: {
        ...project,
        selectedBranchId: branchId,
        branchSelectionSeason: project.selectedSeason,
        availableBranches: remainingBranches.map(b => ({ ...b })),
        promise: branch.promise,
        tradeOff: branch.tradeOff,
      },
    },
  }
}

export const refreshClubStrategyForSeason = (state: GameState): GameState => {
  const season = seasonNumber(state)
  const newStrategy = hydrateClubStrategy(state, state.clubStrategy)
  const newObjective = deriveSeasonObjective(state)
  // For a new season, youth-appearance objectives should start from baseline 0
  // (not counting previous seasons' appearances)
  const adjustedObjective = newObjective.type === 'young-player-appearances'
    ? { ...newObjective, progress: { ...newObjective.progress, baseline: 0 } }
    : newObjective
  return {
    ...state,
    clubStrategy: {
      ...newStrategy,
      objective: adjustedObjective,
      annualPlan: { season, locked: false, confirmed: false },
    },
  }
}
